import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import {User as UserIcon, LogOut, Settings, LogIn, ChevronDown, Package, Calendar, ClipboardList, PenLine, PenOff} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useEditMode } from '@/hooks/useEditMode'
import { lumi } from '@/lib/lumi'

export default function UserMenu() {
  const { isAuthenticated, isAdmin, user } = useAuth()
  const { editMode, toggleEditMode } = useEditMode()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  if (!isAuthenticated) {
    return (
      <button
        onClick={() => lumi.auth.signIn()}
        className="group inline-flex items-center gap-2 px-5 py-2 border border-[#c9a96e]/40 text-[#c9a96e] hover:bg-[#c9a96e] hover:text-[#262626] hover:border-[#c9a96e] text-xs tracking-widest uppercase transition-all duration-500"
      >
        <LogIn className="w-3.5 h-3.5" />
        <span>Connexion</span>
      </button>
    )
  }

  const initial = (user?.userName || user?.email || 'U').charAt(0).toUpperCase()
  const displayName = user?.userName || user?.email?.split('@')[0] || 'Utilisateur'

  return (
    <div className="flex items-center gap-2">
      {isAdmin && (
        <button
          onClick={toggleEditMode}
          className={`hidden sm:inline-flex items-center gap-2 px-3 py-2 border text-xs tracking-widest uppercase transition-all duration-300 ${
            editMode
              ? 'border-[#c9a96e] bg-[#c9a96e] text-[#262626]'
              : 'border-[#c9a96e]/25 text-[#f5f1ea]/80 hover:border-[#c9a96e]/60 hover:text-[#c9a96e]'
          }`}
          title={editMode ? 'Désactiver l\'édition du site' : 'Activer l\'édition du site'}
        >
          {editMode ? <PenOff className="w-3.5 h-3.5" /> : <PenLine className="w-3.5 h-3.5" />}
          <span>{editMode ? 'Édition ON' : 'Éditer'}</span>
        </button>
      )}
      <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2.5 px-3 py-2 border transition-all duration-300 ${
          open
            ? 'border-[#c9a96e] text-[#c9a96e]'
            : 'border-[#c9a96e]/25 text-[#f5f1ea]/80 hover:border-[#c9a96e]/60 hover:text-[#c9a96e]'
        }`}
      >
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#c9a96e] to-[#c9a96e]/60 flex items-center justify-center text-[#262626] text-xs font-semibold">
          {initial}
        </div>
        <span className="hidden md:inline text-xs tracking-widest uppercase max-w-[120px] truncate">
          {displayName}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-3 w-64 bg-[#262626] border border-[#c9a96e]/20 shadow-2xl shadow-black/50 backdrop-blur-xl overflow-hidden animate-[fadeIn_0.2s_ease-out]">
          {/* User card */}
          <div className="px-5 py-4 border-b border-[#c9a96e]/10 bg-gradient-to-br from-[#c9a96e]/5 to-transparent">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#c9a96e] to-[#c9a96e]/60 flex items-center justify-center text-[#262626] text-sm font-semibold">
                {initial}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#f5f1ea] font-light truncate">{displayName}</p>
                <p className="text-xs text-[#a8a39e] truncate">{user?.email}</p>
              </div>
            </div>
            {isAdmin && (
              <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 border border-[#c9a96e]/30 text-[10px] tracking-widest uppercase text-[#c9a96e]/90">
                <span className="w-1 h-1 rounded-full bg-[#c9a96e] shimmer" />
                Administrateur
              </div>
            )}
          </div>

          {/* Menu items */}
          <div className="py-2">
            {isAdmin && (
              <button
                onClick={() => {
                  toggleEditMode()
                  setOpen(false)
                }}
                className="w-full sm:hidden flex items-center gap-3 px-5 py-3 text-sm text-[#f5f1ea]/80 hover:text-[#c9a96e] hover:bg-[#c9a96e]/5 transition-colors"
              >
                {editMode ? <PenOff className="w-4 h-4" /> : <PenLine className="w-4 h-4" />}
                <span className="font-light">{editMode ? 'Désactiver l\'édition' : 'Éditer le site'}</span>
              </button>
            )}
            {isAdmin && (
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-5 py-3 text-sm text-[#f5f1ea]/80 hover:text-[#c9a96e] hover:bg-[#c9a96e]/5 transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span className="font-light">Éditeur de contenu</span>
              </Link>
            )}
            {isAdmin && (
              <Link
                to="/admin/accompagnements"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-5 py-3 text-sm text-[#f5f1ea]/80 hover:text-[#c9a96e] hover:bg-[#c9a96e]/5 transition-colors"
              >
                <Package className="w-4 h-4" />
                <span className="font-light">Accompagnements</span>
              </Link>
            )}
            {isAdmin && (
              <Link
                to="/admin/accompagnements?tab=planning"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-5 py-3 text-sm text-[#f5f1ea]/80 hover:text-[#c9a96e] hover:bg-[#c9a96e]/5 transition-colors"
              >
                <Calendar className="w-4 h-4" />
                <span className="font-light">Planning</span>
              </Link>
            )}
            {isAdmin && (
              <Link
                to="/admin/accompagnements?tab=inscriptions"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-5 py-3 text-sm text-[#f5f1ea]/80 hover:text-[#c9a96e] hover:bg-[#c9a96e]/5 transition-colors"
              >
                <ClipboardList className="w-4 h-4" />
                <span className="font-light">Inscriptions</span>
              </Link>
            )}
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-5 py-3 text-sm text-[#f5f1ea]/80 hover:text-[#c9a96e] hover:bg-[#c9a96e]/5 transition-colors"
            >
              <UserIcon className="w-4 h-4" />
              <span className="font-light">Mon espace</span>
            </Link>
            <button
              onClick={() => {
                setOpen(false)
                lumi.auth.signOut()
              }}
              className="w-full flex items-center gap-3 px-5 py-3 text-sm text-[#f5f1ea]/80 hover:text-red-400 hover:bg-red-500/5 transition-colors border-t border-[#c9a96e]/10 mt-1"
            >
              <LogOut className="w-4 h-4" />
              <span className="font-light">Se déconnecter</span>
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}
